import React, { useState } from 'react';
import { ArrowLeft, ArrowRight, CircleAlert, Pencil, Plus, Trash2 } from 'lucide-react';
import QuestionEditorModal from './Modal';
import QuestionTypeSelectorModal from './QuestionTypeSelectorModal';
import TemplatePreview from './TemplatePreview';
import TemplateSteps from './TemplateSteps';
import SectionEyebrow from '../../common/SectionEyebrow';
import Modal from '../../common/Modal';
import { useTemplate } from '../../../assets/js/TemplateContext';
import { useLanguage } from '../../../assets/js/LanguageContext';
import '../../../assets/styles/ChooseSurvey.css';
import '../../../assets/styles/TemplateBuilder.css';

const buildNewQuestion = (type, questions) => {
  const numericIds = questions
    .map((question) => Number(question.id))
    .filter(Number.isFinite);

  const newId = numericIds.length > 0 ? Math.max(...numericIds) + 1 : questions.length + 1;
  const newQuestion = {
    id: newId.toString(),
    pregunta: '',
    tipo: type,
    configurada: false,
  };

  if (type === 'rating') {
    newQuestion.minValue = 0;
    newQuestion.maxValue = 10;
  }

  if (type === 'closed') {
    newQuestion.opciones = ['', ''];
  }

  return newQuestion;
};

const CreateTemplateStep1 = ({ diagnosticName, predefinedQuestions, onPreviewChange, onEditingQuestion, onBack }) => {
  const { t } = useLanguage();
  const { updateQuestions } = useTemplate();
  const questionTypeLabels = {
    open: t('question_type_open'),
    closed: t('question_type_closed'),
    rating: t('dash_question_type_rating'),
  };
  const [showEditor, setShowEditor] = useState(false);
  const [showTypePicker, setShowTypePicker] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [questions, setQuestions] = useState(predefinedQuestions);
  const [questionToDelete, setQuestionToDelete] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  const launchPreview = () => {
    updateQuestions(questions);
    setShowPreview(true);
    onPreviewChange(true);
  };

  if (showPreview) {
    return (
      <TemplatePreview
        onClose={() => {
          setShowPreview(false);
          onPreviewChange(false);
        }}
      />
    );
  }

  const closeEditor = () => {
    setShowEditor(false);
    setCurrentQuestion(null);
    onEditingQuestion(false);
  };

  const handleEditQuestion = (question) => {
    setCurrentQuestion(question);
    setShowEditor(true);
    onEditingQuestion(true);
  };

  const handleAddQuestionClick = () => {
    setCurrentQuestion(null);
    setShowTypePicker(true);
    onEditingQuestion(true);
  };

  const handleSelectQuestionType = (type) => {
    setShowTypePicker(false);
    setCurrentQuestion(buildNewQuestion(type, questions));
    setShowEditor(true);
    onEditingQuestion(true);
  };

  const closeTypePicker = () => {
    setShowTypePicker(false);
    onEditingQuestion(false);
  };

  const confirmDelete = () => {
    const updatedQuestions = questions.filter((question) => question.id !== questionToDelete.id);
    setQuestions(updatedQuestions);
    updateQuestions(updatedQuestions);
    setQuestionToDelete(null);
  };

  const handleSaveQuestion = (updatedQuestion) => {
    const normalizedQuestion = {
      ...updatedQuestion,
      configurada: true,
      pregunta: updatedQuestion.pregunta || '',
    };

    const questionIndex = questions.findIndex((q) => q.id === normalizedQuestion.id);
    const newQuestions = questionIndex !== -1
      ? questions.map((q) => (q.id === normalizedQuestion.id ? normalizedQuestion : q))
      : [...questions, normalizedQuestion];

    setQuestions(newQuestions);
    updateQuestions(newQuestions);
    closeEditor();
  };

  const pendingCount = questions.filter((question) => !question.configurada || !(question.pregunta || '').trim()).length;

  return (
    <div className="tp-choose tp-builder">
      <button type="button" className="tp-choose__back" onClick={onBack}>
        <ArrowLeft size={16} strokeWidth={2} aria-hidden="true" />
        {t('common_back')}
      </button>

      <header className="tp-choose__head">
        <div className="tp-choose__heading">
          <SectionEyebrow>{t('template_active_kicker')}</SectionEyebrow>
          <h1 className="tp-choose__title">{diagnosticName}</h1>
          <p className="tp-choose__lead">{t('builder_lead')}</p>
        </div>
      </header>

      <TemplateSteps current={2} />

      <section className="tp-builder__panel">
        <div className="tp-builder__panel-head">
          <h2 className="tp-builder__count">
            {t('builder_questions_count').replace('{count}', questions.length)}
          </h2>
          {pendingCount > 0 && (
            <span className="tp-builder__pending">
              <CircleAlert size={14} strokeWidth={2} aria-hidden="true" />
              {t('builder_pending').replace('{count}', pendingCount)}
            </span>
          )}
        </div>

        <ol className="tp-builder__list">
          {questions.map((question, index) => {
            const text = (question.pregunta || '').trim();
            const pending = !question.configurada || !text;
            return (
              <li key={question.id || `${question.tipo}-${index}`} className="tp-builder__item">
                <span className="tp-builder__num" aria-hidden="true">{index + 1}</span>
                <div className="tp-builder__body">
                  <p className={`tp-builder__text ${text ? '' : 'is-empty'}`.trim()}>
                    {text || t('builder_no_text')}
                  </p>
                  <p className="tp-builder__meta">
                    <span className={`tp-dmod__type is-${question.tipo}`}>
                      {questionTypeLabels[question.tipo] || t('question_type_none')}
                    </span>
                    {pending && <span className="tp-builder__tag">{t('template_question_pending')}</span>}
                  </p>
                </div>
                <div className="tp-builder__actions">
                  <button type="button" className="btn btn--outline btn--sm" onClick={() => handleEditQuestion(question)}>
                    <Pencil size={14} strokeWidth={2} aria-hidden="true" />
                    {t('homeqg_edit_modify')}
                  </button>
                  <button type="button" className="btn btn--sm tp-builder__delete" onClick={() => setQuestionToDelete(question)}>
                    <Trash2 size={14} strokeWidth={2} aria-hidden="true" />
                    {t('homeqg_delete')}
                  </button>
                </div>
              </li>
            );
          })}
        </ol>

        <div className="tp-builder__panel-actions">
          <button type="button" className="btn btn--secondary tp-builder__add" onClick={handleAddQuestionClick}>
            <Plus size={16} strokeWidth={2.25} aria-hidden="true" />
            {t('create_add_question')}
          </button>
          <button type="button" className="btn btn--primary" onClick={launchPreview} disabled={questions.length === 0}>
            {t('builder_continue')}
            <ArrowRight size={16} strokeWidth={2} aria-hidden="true" />
          </button>
        </div>
      </section>

      <QuestionTypeSelectorModal
        open={showTypePicker}
        onSelect={handleSelectQuestionType}
        onClose={closeTypePicker}
      />

      <QuestionEditorModal
        question={showEditor ? currentQuestion : null}
        onClose={closeEditor}
        onSave={handleSaveQuestion}
      />

      <Modal
        open={Boolean(questionToDelete)}
        onOpenChange={(open) => { if (!open) setQuestionToDelete(null); }}
        variant="delete"
        title={t('builder_delete_title')}
        description={(questionToDelete?.pregunta || '').trim() || t('builder_no_text')}
        cancelText={t('common_cancel')}
        confirmText={t('homeqg_delete')}
        onConfirm={confirmDelete}
      />
    </div>
  );
};

export default CreateTemplateStep1;
